"use client"
import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import { Slider } from "@/components/ui/Slider";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export const options = {
  responsive: true,
  plugins: {
    legend: {
      position: "top",
    },
    title: {
      display: false,
      text: "Chart.js Line Chart",
    },
  },
};

const InvestmentCalculator = ({ months = 12, initialInvestmentValue = 2000,initialMonthlyContribution=500 }) => {
  const [initialInvestment, setInitialInvestment] = useState(initialInvestmentValue);
  const [growthValue, setGrowthValue] = useState(10);
  const [monthlyContribution, setMonthlyContribution] = useState(initialMonthlyContribution); // New state for monthly contribution
  const [investment, setInvestment] = useState([]);

  const GraphDataCalculator = () => {
    const investments = Array.from({ length: months }, (_, month) =>
      MonthDataCalculator(month + 1, initialInvestment, growthValue, monthlyContribution)
    );

    setInvestment(investments);
  };

  const MonthDataCalculator = (month, initialInvestment, growthValue, monthlyContribution) => {
    const monthlyRate = growthValue / 12 / 100; // Monthly growth rate as a decimal
    let amount = initialInvestment * Math.pow(1 + monthlyRate, month);

    // Add monthly contributions compounded monthly
    for (let i = 1; i <= month; i++) {
      amount += monthlyContribution * Math.pow(1 + monthlyRate, month - i);
    }

    return amount;
  };

  useEffect(() => {
    GraphDataCalculator();
  }, [initialInvestment, growthValue, monthlyContribution, months]);

  const generateMonthLabels = (months) => {
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const currentMonth = new Date().getMonth();
    return Array.from({ length: months }, (_, i) => monthNames[(currentMonth + i) % 12]);
  };

  return (
    <div>
      <h4 className="text-xl font-bold">Value Calculator</h4>
      <div className="flex flex-col text-center">
        <p>Projected supply return of</p>
        <p className="font-bold">
          R {MonthDataCalculator(months, initialInvestment, growthValue, monthlyContribution).toFixed(2)} in {months} Months
        </p>
      </div>

      <div>
        <Line
          options={options}
          className="max-h-[50vh]"
          data={{
            labels: generateMonthLabels(months),
            datasets: [
              {
                label: "Investment Growth",
                data: investment,
                borderColor: "#1DAEEF",
                stack: "2",
              },
            ],
          }}
        />
      </div>

      <div className="flex md:flex-row gap-4 mt-4 flex-col">
        <div className="w-full">
          <div className="mb-3">
            <h6 className="font-bold">Initial Supply</h6>
            <p>R {initialInvestment}</p>
          </div>
          <Slider
            onValueCommit={() => GraphDataCalculator()}
            defaultValue={[initialInvestment]}
            value={[initialInvestment]}
            max={10000}
            min={100}
            onValueChange={(x) => setInitialInvestment(x[0])}
            step={100}
          />
        </div>

        <div className="w-full">
          <div className="mb-3">
            <h6 className="font-bold">Supply Value Growth</h6>
            <p>{growthValue}%</p>
          </div>
          <Slider
            onValueCommit={() => GraphDataCalculator()}
            defaultValue={[growthValue]}
            value={[growthValue]}
            max={15}
            min={2}
            disabled={true}
            onValueChange={(x) => setGrowthValue(x[0])}
            step={1}
          />
        </div>

        <div className="w-full">
          <div className="mb-3">
            <h6 className="font-bold">Monthly Contribution</h6>
            <p>R {monthlyContribution}</p>
          </div>
          <Slider
            onValueCommit={() => GraphDataCalculator()}
            defaultValue={[monthlyContribution]}
            value={[monthlyContribution]}
            max={2000}
            min={0}
            onValueChange={(x) => setMonthlyContribution(x[0])}
            step={50}
          />
        </div>
      </div>
    </div>
  );
};

export default InvestmentCalculator;
